import { useEffect, useMemo, useState } from 'react';
import { Document } from '@contentful/rich-text-types';
import { Accordion as AccordionBtn, SideMenu } from '@loomstateorg/shared-frontend';
import { ContentImage } from '../../types';
import ContentSection from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content-section';

type SectionGroup = { type: 'accordion' | 'regular'; sections: StaticSection[] };

export interface StaticSection {
  id: string;
  title?: string;
  subtitle?: string;
  subtitlePosition?: 'left' | 'top';
  /** Contentful rich text document or Sanity portable text blocks */
  content?: Document | any[];
  displaySectionAsAccordion?: boolean;
  accordionTitle?: string;
  tableHeadColor?: string;
  tableHeadTextColor?: string;
  tableBodyColor?: string;
  tableBodyTextColor?: string;
  textContentWidth?: '1/4' | '1/3' | '1/2' | '2/3' | '3/4' | 'full';
  images?: ContentImage[];
  imageLayout?: 'inline' | 'grid';
  image?: string;
  imageSrc?: string;
  imageAlt?: string;
  imagePosition?: 'left' | 'right' | 'centered';
  imageWidth?: '1/4' | '1/3' | '1/2' | 'full';
  pdfFile?: any;
}

export interface MenuItem {
  id: string;
  label: string;
  href: string;
}

export interface StaticPageData {
  pageTitle?: string;
  navigationTitle: string;
  navigation: MenuItem[];
  sections: StaticSection[];
}

interface StaticPageContentProps {
  initialData: StaticPageData;
  accordionButtonClassName?: string;
}

const StaticPageContent: React.FC<StaticPageContentProps> = ({
  initialData,
  accordionButtonClassName = 'font-medium text-16 py-20 px-16 rounded-md',
}) => {
  const [activeSection, setActiveSection] = useState<string>(initialData.navigation[0]?.id || '');

  const groupedSections = useMemo<SectionGroup[]>(() => {
    const groups: SectionGroup[] = [];
    for (const section of initialData.sections) {
      const type = section.displaySectionAsAccordion ? 'accordion' : 'regular';
      const lastGroup = groups[groups.length - 1];

      if (type === 'accordion' && lastGroup?.type === 'accordion') {
        lastGroup.sections.push(section);
      } else {
        groups.push({ type, sections: [section] });
      }
    }
    return groups;
  }, [initialData.sections]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom >= 150) {
          setActiveSection(section.id);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!initialData?.sections?.length) {
    return <div data-testid="empty-state">No content available</div>;
  }

  return (
    <div className="grid grid-cols-1 md:gap-52 lg:grid-cols-8" role="document" data-testid="static-page">
      {initialData?.navigation?.length > 0 && (
        <SideMenu
          items={initialData.navigation}
          navigationTitle={initialData.navigationTitle}
          activeId={activeSection}
          className={'hidden lg:block'}
        />
      )}

      <main
        className={`my-32 ${initialData?.navigation?.length ? 'lg:col-span-6' : 'lg:col-span-8'}`}
        aria-label={initialData?.pageTitle || 'Page content'}
      >
        {initialData?.pageTitle && (
          <h1 className="pb-16 text-left font-body text-28 leading-normal" id="sizeGuides" data-testid="page-title">
            {initialData?.pageTitle}
          </h1>
        )}
        {groupedSections.map((group, groupIndex) => {
          if (group.type === 'accordion') {
            return (
              <section
                key={`accordion-group-${groupIndex}`}
                className="grid gap-y-16 pb-24 md:pb-36 lg:pb-16"
                data-testid={`accordion-group-${groupIndex}`}
                aria-label={`Accordion group ${groupIndex + 1}`}
              >
                {group.sections.map((section) => (
                  <article
                    key={section.id}
                    data-section
                    data-testid={`accordion-section-${section.id}`}
                    id={section.id}
                    className="rounded-lg border border-neutral-400 shadow-lg"
                    aria-labelledby={`accordion-heading-${section.id}`}
                  >
                    <h2 id={`accordion-heading-${section.id}`} className="sr-only">
                      {section.accordionTitle || section.title || section.id}
                    </h2>
                    <AccordionBtn
                      variant="plusAndMinus"
                      className="text-24"
                      iconClassName="text-primary-regular"
                      panelClassName="font-primaryThin text-16"
                      closedSectionTitle={section.accordionTitle}
                      buttonClassName={accordionButtonClassName}
                    >
                      <ContentSection
                        accordionButtonClassName={accordionButtonClassName}
                        content={section.content}
                        tableHeadColor={section.tableHeadColor}
                        tableHeadTextColor={section.tableHeadTextColor}
                        tableBodyColor={section.tableBodyColor}
                        tableBodyTextColor={section.tableBodyTextColor}
                        textContentWidth={section.textContentWidth}
                        images={section.images}
                        imageLayout={section.imageLayout}
                        imagePosition={section.imagePosition}
                        imageWidth={section.imageWidth}
                        pdfFile={section.pdfFile}
                        className="py-20 pl-16 pr-24 font-primaryThin"
                      />
                    </AccordionBtn>
                  </article>
                ))}
              </section>
            );
          }

          const section = group.sections[0];
          return (
            <section
              key={section.id}
              data-section
              data-testid={`content-section-${section.id}`}
              id={section.id}
              className={`${initialData.sections.length > 1 ? 'mb-16' : ''}`}
              aria-labelledby={`section-heading-${section.id}`}
            >
              {section.title && (
                <span id={`section-heading-${section.id}`} className="sr-only">
                  {section.title}
                </span>
              )}
              <ContentSection
                accordionButtonClassName={accordionButtonClassName}
                title={section.title}
                subtitle={section.subtitle}
                subtitlePosition={section.subtitlePosition}
                content={section.content}
                displaySectionAsAccordion={section.displaySectionAsAccordion}
                accordionTitle={section.accordionTitle}
                tableHeadColor={section.tableHeadColor}
                tableHeadTextColor={section.tableHeadTextColor}
                tableBodyColor={section.tableBodyColor}
                tableBodyTextColor={section.tableBodyTextColor}
                textContentWidth={section.textContentWidth}
                images={section.images}
                imageLayout={section.imageLayout}
                imagePosition={section.imagePosition}
                imageWidth={section.imageWidth}
                pdfFile={section.pdfFile}
              />
            </section>
          );
        })}
      </main>
    </div>
  );
};

export default StaticPageContent;
